import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import type { TripView } from '@voll-intelligence/domain';
import {
  calendarDateFromUtcDay,
  formatTripRange,
  money,
  tripName,
} from '@voll-intelligence/trip-panel';

import { t } from '@web/i18n';
import { AppIcons, Button, type AppIconName } from '@web/ui/core/atoms';
import { DataTable, StatusBadge, ToneChip, tripStatusBadge } from '@web/ui/core/molecules';
import { fireAndForget } from '@web/ui/core/utils';

const MODAL_ICON: Partial<Record<string, AppIconName>> = {
  FLIGHT: 'airplane',
  HOTEL: 'bed',
  RENTAL: 'car',
};
const MODAL_LABEL = {
  FLIGHT: 'travelerView.modal.flight',
  HOTEL: 'travelerView.modal.hotel',
  RENTAL: 'travelerView.modal.rental',
} as const;

function modalLabel(modal: string): string {
  return modal in MODAL_LABEL
    ? t(MODAL_LABEL[modal as keyof typeof MODAL_LABEL])
    : t('travelerView.modal.other');
}

function TripStatus({ status }: Readonly<{ status: TripView['status'] }>): React.JSX.Element {
  if (!status) return <span className="text-faint-foreground">—</span>;
  const { tone, labelKey } = tripStatusBadge(status);
  return <StatusBadge tone={tone}>{t(labelKey)}</StatusBadge>;
}

function tripColumns(
  conversationTripId: string | undefined,
  onSelect: (trip: TripView) => void,
): ColumnDef<TripView>[] {
  return [
    {
      id: 'trip',
      header: () => t('travelerView.trips.col.trip'),
      cell: ({ row }) => (
        <span className="flex items-center gap-2">
          {/* The row click is for the pointer; the name is the keyboard's way in. */}
          <button
            type="button"
            className="rounded-sm text-left font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(row.original);
            }}
          >
            {tripName(row.original)}
          </button>
          {row.original.id === conversationTripId ? (
            <ToneChip tone="ai">{t('travelerView.trips.thisConversation')}</ToneChip>
          ) : null}
        </span>
      ),
    },
    {
      id: 'dates',
      header: () => t('travelerView.trips.col.dates'),
      cell: ({ row }) =>
        formatTripRange(
          calendarDateFromUtcDay(row.original.startAt),
          calendarDateFromUtcDay(row.original.endAt),
        ) || '—',
    },
    {
      id: 'services',
      header: () => t('travelerView.trips.col.services'),
      cell: ({ row }) => (
        // `relative`: the `sr-only` label is absolutely positioned; without a
        // positioned ancestor inside the scrolling half it escapes the clip and
        // stretches the document, which then scrolls under the header.
        <span className="relative flex items-center gap-1 text-muted-foreground">
          {row.original.modals.map((m) => (
            <AppIcons key={m} name={MODAL_ICON[m] ?? 'package'} size={14} />
          ))}
          <span className="sr-only">{row.original.modals.map(modalLabel).join(', ')}</span>
        </span>
      ),
    },
    {
      id: 'status',
      header: () => t('travelerView.trips.col.status'),
      cell: ({ row }) => <TripStatus status={row.original.status} />,
    },
    {
      id: 'amount',
      header: () => t('travelerView.trips.col.amount'),
      cell: ({ row }) => money(row.original.totalAmount, row.original.currency) ?? '—',
    },
  ];
}

/** "Todas as viagens": a row click puts that trip on top of the right half. */
export function TravelerTripsTable({
  query,
  conversationTripId,
  activeTripId,
  onSelect,
}: Readonly<{
  query: UseQueryResult<TripView[]>;
  conversationTripId: string | undefined;
  activeTripId: string | null;
  onSelect: (trip: TripView) => void;
}>): React.JSX.Element {
  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-center gap-2">
        <AppIcons name="ticket" size={14} className="text-faint-foreground" />
        <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-muted-foreground">
          {t('travelerView.trips.title', { count: query.data?.length ?? 0 })}
        </span>
      </header>
      <TripsBody
        query={query}
        conversationTripId={conversationTripId}
        activeTripId={activeTripId}
        onSelect={onSelect}
      />
    </section>
  );
}

function TripsBody({
  query,
  conversationTripId,
  activeTripId,
  onSelect,
}: Readonly<{
  query: UseQueryResult<TripView[]>;
  conversationTripId: string | undefined;
  activeTripId: string | null;
  onSelect: (trip: TripView) => void;
}>): React.JSX.Element {
  // Stable columns: `flexRender` mounts each `cell` as a component, so a fresh
  // function per render remounts every cell — the trip button loses focus, and
  // a click landing during the re-render is lost.
  const columns = useMemo(
    () => tripColumns(conversationTripId, onSelect),
    [conversationTripId, onSelect],
  );
  if (query.isPending) {
    return <p className="m-0 text-meta text-muted-foreground">{t('travelerView.trips.loading')}</p>;
  }
  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="m-0 text-meta text-tone-red-fg">{t('travelerView.trips.error')}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fireAndForget(query.refetch());
          }}
        >
          {t('travelerView.trips.retry')}
        </Button>
      </div>
    );
  }
  return (
    <DataTable
      columns={columns}
      data={query.data}
      getRowId={(trip) => trip.id}
      activeRowId={activeTripId}
      onRowClick={onSelect}
      emptyMessage={t('travelerView.trips.empty')}
    />
  );
}
